import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import {
    stcBinTypeService,
    StcBinTypeDto,
    BulkUpdateStcBinTypeRequest,
    BulkDeleteResult
} from '../../../../../services/stcBinTypeService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface SelectedRows {
    [key: string]: boolean;
}

interface MasterDataLookups {
    binGroups: Map<number, { binGrpNo: number; description1?: string }>;
    priorities: Map<number, { priorityNo: number; description1?: string }>;
    movementTypes: Map<number, { movementTypeNo: number; description1?: string }>;
}

interface BinTypeSidePanelProps {
    selectedRows: SelectedRows;
    data: StcBinTypeDto[];
    masterData?: MasterDataLookups | null;
    onClose: () => void;
    onSave: () => void;
}

interface BulkEditConfig {
    [key: string]: {
        enabled: boolean;
        value: any;
    };
}

type FieldType = 'text' | 'number' | 'boolean' | 'select';

interface FieldDefinition {
    key: string;
    label: string;
    type: FieldType;
    singleOnly?: boolean;
    options?: { value: number; label: string }[];
}

export const BinTypeSidePanel: React.FC<BinTypeSidePanelProps> = ({
    selectedRows,
    data,
    masterData,
    onClose,
    onSave
}) => {
    const { t } = useTranslation();
    const [bulkEditConfig, setBulkEditConfig] = useState<BulkEditConfig>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

    const selectedRowCount = Object.values(selectedRows).filter(Boolean).length;
    const selectedData = data.filter(item => selectedRows[item.id.toString()]);
    const isSingleSelection = selectedRowCount === 1;
    const singleItem = isSingleSelection ? selectedData[0] : null;

    // Build dropdown options from master data
    const binGrpOptions = useMemo(() =>
        masterData ? Array.from(masterData.binGroups.values()).map(g => ({
            value: g.binGrpNo, label: `${g.binGrpNo} - ${g.description1 || ''}`
        })) : [], [masterData]);

    const priorityOptions = useMemo(() =>
        masterData ? Array.from(masterData.priorities.values()).map(p => ({
            value: p.priorityNo, label: `${p.priorityNo} - ${p.description1 || ''}`
        })) : [], [masterData]);

    const movementTypeOptions = useMemo(() =>
        masterData ? Array.from(masterData.movementTypes.values()).map(m => ({
            value: m.movementTypeNo, label: `${m.movementTypeNo} - ${m.description1 || ''}`
        })) : [], [masterData]);

    // Field definitions with proper types and dropdown options
    const fields: FieldDefinition[] = useMemo(() => [
        { key: 'binTypeNo', label: t('manager.modules.adminBins.binType.fields.typeNo', {}, 'Type nr.'), type: 'number', singleOnly: true },
        { key: 'description1', label: t('common.fields.description', {}, 'Beskrivelse'), type: 'text' },
        { key: 'binGrpNo', label: t('manager.modules.adminBins.binType.fields.binGroup', {}, 'Lokasjonsgruppe'), type: 'select', options: binGrpOptions },
        { key: 'pickPriorityNo', label: t('manager.modules.adminBins.binType.fields.pickPriority', {}, 'Plukkprioritet'), type: 'select', options: priorityOptions },
        { key: 'putAwayPriorityNo', label: t('manager.modules.adminBins.binType.fields.putAwayPriority', {}, 'Plasseringsprioritet'), type: 'select', options: priorityOptions },
        { key: 'movementTypeNo', label: t('manager.modules.adminBins.binType.fields.movementType', {}, 'Bevegelseskode'), type: 'select', options: movementTypeOptions },
        { key: 'pickCustomOrder', label: t('manager.modules.adminBins.binType.fields.pickOrder', {}, 'Plukkrekkefølge'), type: 'number' },
        { key: 'putAwayCustomOrder', label: t('manager.modules.adminBins.binType.fields.putAwayOrder', {}, 'Innlagringsrekkefølge'), type: 'number' },
        { key: 'reservationAllowed', label: t('manager.modules.adminBins.binType.fields.reservationAllowed', {}, 'Reservasjon tillatt'), type: 'boolean' },
        { key: 'negativeReservationAllowed', label: t('manager.modules.adminBins.binType.fields.negativeReservationAllowed', {}, 'Negativ reservasjon tillatt'), type: 'boolean' },
        { key: 'autoReplenishment', label: t('manager.modules.adminBins.binType.fields.autoReplenishment', {}, 'Auto-påfylling'), type: 'boolean' },
        { key: 'pickableYesNo', label: t('manager.modules.adminBins.binType.fields.pickable', {}, 'Plukkbar'), type: 'boolean' },
        { key: 'sharedBinYesNo', label: t('manager.modules.adminBins.binType.fields.sharedBin', {}, 'Felleslokasjon'), type: 'boolean' },
        { key: 'sharedBinMaxQuantity', label: t('manager.modules.adminBins.binType.fields.maxQtySharedBin', {}, 'Maks ant. felleslok.'), type: 'number' },
        { key: 'sharedBinPreferEmptyBin', label: t('manager.modules.adminBins.binType.fields.preferEmptySharedBin', {}, 'Prioriter tomme felleslok.'), type: 'boolean' },
        { key: 'replenishMinLevel', label: t('manager.modules.adminBins.binType.fields.replenishMinPercent', {}, 'Påfylling min %'), type: 'number' },
        { key: 'replenishMaxLevel', label: t('manager.modules.adminBins.binType.fields.replenishMaxPercent', {}, 'Påfylling maks %'), type: 'number' },
        { key: 'replenishMaxBins', label: t('manager.modules.adminBins.binType.fields.maxReplenishBins', {}, 'Maks påfyllingslok.'), type: 'number' },
        { key: 'replenishFixedBins', label: t('manager.modules.adminBins.binType.fields.fixedReplenishBins', {}, 'Faste påfyllingslok.'), type: 'boolean' },
        { key: 'replenishMaxBinsSource', label: t('manager.modules.adminBins.binType.fields.maxSourceBins', {}, 'Maks kildelok.'), type: 'number' }
    ], [binGrpOptions, priorityOptions, movementTypeOptions, t]);

    // Get default value for a field type
    const getDefaultValue = (field: FieldDefinition): any => {
        if (field.type === 'text') return '';
        if (field.type === 'boolean') return false;
        if (field.type === 'number' || field.type === 'select') {
            if (field.key === 'pickPriorityNo' || field.key === 'putAwayPriorityNo') return 1;
            return undefined;
        }
        return '';
    };

    // Initialize bulk edit config when component mounts - pre-fill when single selection
    useEffect(() => {
        const initialConfig: BulkEditConfig = {};
        fields.forEach(field => {
            if (isSingleSelection && singleItem) {
                initialConfig[field.key] = {
                    enabled: true,
                    value: (singleItem as any)[field.key] ?? getDefaultValue(field)
                };
            } else {
                initialConfig[field.key] = {
                    enabled: false,
                    value: getDefaultValue(field)
                };
            }
        });
        setBulkEditConfig(initialConfig);
    }, []);

    const handleFieldToggle = (fieldKey: string, enabled: boolean) => {
        setBulkEditConfig(prev => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                enabled
            }
        }));
    };

    const handleFieldValueChange = (field: FieldDefinition, value: any) => {
        let parsedValue = value;
        if (field.type === 'number' || field.type === 'select') {
            parsedValue = value !== '' && value !== undefined && value !== null ? parseInt(value) : undefined;
        } else if (field.type === 'boolean') {
            parsedValue = value === 'true' || value === true;
        }
        setBulkEditConfig(prev => ({
            ...prev,
            [field.key]: {
                ...prev[field.key],
                value: parsedValue
            }
        }));
    };

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            setError('');

            const enabledFields = Object.entries(bulkEditConfig)
                .filter(([, config]) => config.enabled)
                .map(([field, config]) => ({ field, value: config.value }));

            if (enabledFields.length === 0) {
                setError(t('manager.modules.adminBins.common.errors.noFieldsSelected', {}, 'Ingen felter valgt for oppdatering'));
                return;
            }

            // binTypeNo can only be changed for single selection
            if (enabledFields.some(f => f.field === 'binTypeNo') && selectedRowCount > 1) {
                setError(t('manager.modules.adminBins.binType.errors.typeNoSingleOnly', {}, 'Type nr. kan kun endres for én lokasjonstype om gangen'));
                return;
            }

            // Create bulk update requests
            const bulkRequests: BulkUpdateStcBinTypeRequest[] = selectedData.map(item => {
                const request: BulkUpdateStcBinTypeRequest = {
                    companyNo: item.companyNo,
                    binTypeNo: item.binTypeNo,
                    binGrpNo: item.binGrpNo,
                    pickPriorityNo: item.pickPriorityNo,
                    pickCustomOrder: item.pickCustomOrder,
                    replenishMinLevel: item.replenishMinLevel,
                    movementTypeNo: item.movementTypeNo,
                    putAwayCustomOrder: item.putAwayCustomOrder,
                    replenishMaxLevel: item.replenishMaxLevel,
                    putAwayPriorityNo: item.putAwayPriorityNo,
                    reservationAllowed: item.reservationAllowed,
                    negativeReservationAllowed: item.negativeReservationAllowed,
                    autoReplenishment: item.autoReplenishment,
                    pickableYesNo: item.pickableYesNo,
                    sharedBinYesNo: item.sharedBinYesNo,
                    sharedBinMaxQuantity: item.sharedBinMaxQuantity,
                    replenishMaxBins: item.replenishMaxBins,
                    replenishFixedBins: item.replenishFixedBins,
                    replenishMaxBinsSource: item.replenishMaxBinsSource,
                    sharedBinPreferEmptyBin: item.sharedBinPreferEmptyBin,
                    description1: item.description1
                };

                // Apply enabled field changes
                enabledFields.forEach(({ field, value }) => {
                    (request as any)[field] = value;
                });

                return request;
            });

            await stcBinTypeService.bulkUpdate(bulkRequests);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1500);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminBins.common.errors.updateFailed', {}, 'Feil ved oppdatering'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');

            const idsToDelete = selectedData.map(item => item.id);
            const result: BulkDeleteResult = await stcBinTypeService.bulkDelete(idsToDelete);

            if (result.notFoundIds.length > 0) {
                setError(t('manager.modules.adminBins.common.partialDeleteResult', { '0': result.deletedCount, '1': result.requestedCount, '2': result.notFoundIds.length }, `${result.deletedCount} av ${result.requestedCount} rader ble slettet. ${result.notFoundIds.length} rader ble ikke funnet.`));
            } else {
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                    onSave();
                }, 1500);
            }
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminBins.common.errors.deleteFailed', {}, 'Feil ved sletting'));
        } finally {
            setIsDeleting(false);
        }
    };

    const enabledFieldCount = Object.values(bulkEditConfig).filter(config => config.enabled).length;

    const renderFieldInput = (field: FieldDefinition, config: { enabled: boolean; value: any }) => {
        if (!config.enabled) return null;

        const isDisabledSingleOnly = field.singleOnly && selectedRowCount > 1;

        if (field.type === 'select' && field.options && field.options.length > 0) {
            return (
                <div className="mt-2">
                    <select
                        value={config.value ?? ''}
                        onChange={(e) => handleFieldValueChange(field, e.target.value)}
                        disabled={isDisabledSingleOnly}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                        <option value="">{t('manager.modules.adminBins.binType.selectGeneric', {}, '-- Velg --')}</option>
                        {field.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }

        if (field.type === 'boolean') {
            return (
                <div className="mt-2">
                    <select
                        value={config.value?.toString() ?? 'false'}
                        onChange={(e) => handleFieldValueChange(field, e.target.value)}
                        disabled={isDisabledSingleOnly}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                        <option value="true">{t('manager.modules.adminBins.common.yes', {}, 'Ja')}</option>
                        <option value="false">{t('manager.modules.adminBins.common.no', {}, 'Nei')}</option>
                    </select>
                </div>
            );
        }

        if (field.type === 'number') {
            return (
                <div className="mt-2">
                    <input
                        type="number"
                        value={config.value ?? ''}
                        onChange={(e) => handleFieldValueChange(field, e.target.value)}
                        disabled={isDisabledSingleOnly}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t('manager.modules.adminBins.common.newValueFor', { '0': field.label }, `Ny verdi for ${field.label}`)}
                    />
                </div>
            );
        }

        return (
            <div className="mt-2">
                <input
                    type="text"
                    value={config.value ?? ''}
                    onChange={(e) => handleFieldValueChange(field, e.target.value)}
                    disabled={isDisabledSingleOnly}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Ny verdi for ${field.label}`}
                />
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

            <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {isSingleSelection ? t('manager.modules.adminBins.binType.edit.title', {}, 'Rediger lokasjonstype') : t('manager.modules.adminBins.common.bulkUpdate', {}, 'Masseoppdatering')}
                        </h2>
                        <p className="text-sm text-gray-600">
                            {t('manager.modules.adminBins.common.rowsSelectedIn', { '0': selectedRowCount, '1': selectedRowCount === 1 ? t('manager.modules.adminBins.common.row', {}, 'rad') : t('manager.modules.adminBins.common.rows', {}, 'rader'), '2': t('manager.modules.adminBins.binType.moduleName', {}, 'Lokasjonstyper') }, `${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'} valgt i Lokasjonstyper`)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('edit')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'edit'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        {t('common.buttons.edit', {}, 'Rediger')}
                    </button>
                    <button
                        onClick={() => setActiveTab('delete')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'delete'
                                ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        {t('common.buttons.delete', {}, 'Slett')}
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'edit' ? (
                        <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-blue-800">
                                        <p className="font-medium mb-1">
                                            {isSingleSelection ? t('manager.modules.adminBins.common.editing', {}, 'Redigering') : t('manager.modules.adminBins.common.bulkUpdate', {}, 'Masseoppdatering')}
                                        </p>
                                        <p>
                                            {isSingleSelection
                                                ? t('manager.modules.adminBins.common.editIntro', {}, 'Alle felt er forhåndsutfylt med gjeldende verdier. Endre det du ønsker og lagre.')
                                                : t('manager.modules.adminBins.common.bulkUpdateIntro', {}, 'Velg feltene du ønsker å oppdatere for alle valgte rader. Kun markerte felt vil bli endret.')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Field Editor */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-gray-900">{t('manager.modules.adminBins.common.fieldsToUpdate', {}, 'Felter som skal oppdateres')}</h3>

                                {fields.map(field => {
                                    const config = bulkEditConfig[field.key];
                                    if (!config) return null;

                                    return (
                                        <div
                                            key={field.key}
                                            className={`border rounded-lg p-3 transition-colors ${
                                                config.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            {/* Field Toggle */}
                                            <div className="flex items-center justify-between">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={config.enabled}
                                                        onChange={(e) => handleFieldToggle(field.key, e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {field.label}
                                                    </span>
                                                </label>
                                                {field.singleOnly && selectedRowCount > 1 && config.enabled && (
                                                    <span className="text-xs text-amber-600">{t('manager.modules.adminBins.common.singleSelectOnly', {}, 'Kun ved enkeltvalg')}</span>
                                                )}
                                            </div>

                                            {/* Field Value Input */}
                                            {renderFieldInput(field, config)}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Summary */}
                            {enabledFieldCount > 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-green-800">
                                            <p className="font-medium">{t('manager.modules.adminBins.common.readyToSave', {}, 'Klar til å lagre')}</p>
                                            <p>
                                                {t('manager.modules.adminBins.common.fieldsWillUpdateRows', { '0': enabledFieldCount, '1': selectedRowCount, '2': selectedRowCount === 1 ? t('manager.modules.adminBins.common.row', {}, 'rad') : t('manager.modules.adminBins.common.rows', {}, 'rader') }, `${enabledFieldCount} felt vil bli oppdatert for ${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'}`)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium mb-1">{t('manager.modules.adminBins.common.bulkDelete', {}, 'Massesletting')}</p>
                                        <p>{t('manager.modules.adminBins.common.bulkDeleteRowsIntro', { '0': selectedRowCount, '1': selectedRowCount === 1 ? t('manager.modules.adminBins.common.row', {}, 'rad') : t('manager.modules.adminBins.common.rows', {}, 'rader') }, `Du er i ferd med å slette ${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'}. Denne handlingen kan ikke angres.`)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Selected Items List */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-900">{t('manager.modules.adminBins.common.rowsToDelete', {}, 'Rader som vil bli slettet:')}</h3>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                    {selectedData.map(item => (
                                        <div key={item.id} className="text-xs bg-gray-50 p-2 rounded border">
                                            {t('manager.modules.adminBins.binType.binTypeLabel', { '0': item.binTypeNo, '1': item.description1 || t('manager.modules.adminBins.binType.noDescription', {}, 'Ingen beskrivelse') }, `Lokasjonstype ${item.binTypeNo} - ${item.description1 || 'Ingen beskrivelse'}`)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                        </button>

                        {activeTab === 'edit' ? (
                            <button
                                onClick={handleBulkUpdate}
                                disabled={enabledFieldCount === 0 || isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    enabledFieldCount === 0 || isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('common.status.saving', {}, 'Lagrer...')}
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        {t('manager.modules.adminBins.common.saved', {}, 'Lagret!')}
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        {t('manager.modules.adminBins.common.saveChanges', {}, 'Lagre endringer')}
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isDeleting
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('manager.modules.adminBins.common.deleting', {}, 'Sletter...')}
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        {t('manager.modules.adminBins.common.deleted', {}, 'Slettet!')}
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        {t('manager.modules.adminBins.common.deleteRowsButton', { '0': selectedRowCount, '1': selectedRowCount === 1 ? t('manager.modules.adminBins.common.row', {}, 'rad') : t('manager.modules.adminBins.common.rows', {}, 'rader') }, `Slett ${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'}`)}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
